import type {
  VisibilityAction,
  VisibilityOperator,
} from "../../types/visibility";
import { resolveFieldType } from "../forms/normalizeFieldType";

export const VISIBILITY_OPERATORS: Array<{
  value: VisibilityOperator;
  label: string;
  symbol: string;
}> = [
  { value: "equals", label: "равно", symbol: "=" },
  { value: "not_equals", label: "не равно", symbol: "≠" },
  { value: "empty", label: "не заполнено", symbol: "∅" },
  { value: "not_empty", label: "заполнено", symbol: "●" },
  { value: "filled", label: "заполнено", symbol: "●" },
  { value: "contains", label: "содержит", symbol: "⊂" },
  { value: "in", label: "одно из", symbol: "∈" },
  { value: "gt", label: "больше", symbol: ">" },
  { value: "gte", label: "больше или равно", symbol: "≥" },
  { value: "lt", label: "меньше", symbol: "<" },
  { value: "lte", label: "меньше или равно", symbol: "≤" },
];

/** not_empty и filled — одно и то же на сервере; в UI оставляем одно «заполнено». */
export const VISIBILITY_OPERATOR_OPTIONS = VISIBILITY_OPERATORS.filter(
  (item) => item.value !== "filled",
);

export function visibilityOperatorForUi(
  operator: VisibilityOperator | "",
): VisibilityOperator {
  if (operator === "filled") return "not_empty";
  if (!operator) return "equals";
  return operator;
}

export function visibilityOperatorSymbol(operator: VisibilityOperator): string {
  const current = visibilityOperatorForUi(operator);
  return (
    VISIBILITY_OPERATOR_OPTIONS.find((item) => item.value === current)
      ?.symbol ?? "="
  );
}

export function visibilityOperatorLabel(operator: VisibilityOperator): string {
  const current = visibilityOperatorForUi(operator);
  return (
    VISIBILITY_OPERATOR_OPTIONS.find((item) => item.value === current)?.label ??
    current
  );
}

export function visibilityOperatorNeedsValue(
  operator: VisibilityOperator,
): boolean {
  return (
    operator !== "empty" && operator !== "not_empty" && operator !== "filled"
  );
}

const BASE_OPERATORS: VisibilityOperator[] = [
  "equals",
  "not_equals",
  "empty",
  "not_empty",
];

const ORDERED_OPERATORS: VisibilityOperator[] = ["gt", "gte", "lt", "lte"];

/** Какие сравнения имеют смысл для типа поля-источника. */
export function visibilityOperatorsAllowedForType(
  type: string | null | undefined,
): Set<VisibilityOperator> | null {
  const resolved = resolveFieldType(type);
  if (!resolved) return null;

  switch (resolved) {
    case "display":
      return new Set();
    case "privacy":
      return new Set(BASE_OPERATORS);
    case "choice":
    case "select":
    case "city":
      return new Set([...BASE_OPERATORS, "in"]);
    case "multiple-choice":
      return new Set([...BASE_OPERATORS, "contains", "in"]);
    case "date-range":
      return new Set(["empty", "not_empty"]);
    case "number":
      return new Set([...BASE_OPERATORS, ...ORDERED_OPERATORS, "in"]);
    case "date":
    case "birthdate":
      return new Set([...BASE_OPERATORS, ...ORDERED_OPERATORS]);
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "fio":
      return new Set([...BASE_OPERATORS, "contains"]);
    default:
      return null;
  }
}

export function isDateLikeFieldType(type: string | null | undefined): boolean {
  const resolved = resolveFieldType(type);
  return resolved === "date" || resolved === "birthdate";
}

export function visibilityOperatorAllowedForType(
  operator: VisibilityOperator,
  type: string | null | undefined,
): boolean {
  const allowed = visibilityOperatorsAllowedForType(type);
  if (allowed == null) return true;
  return allowed.has(visibilityOperatorForUi(operator));
}

export function firstAllowedVisibilityOperator(
  type: string | null | undefined,
  preferred: VisibilityOperator = "equals",
): VisibilityOperator {
  if (visibilityOperatorAllowedForType(preferred, type)) {
    return visibilityOperatorForUi(preferred);
  }
  const allowed = visibilityOperatorsAllowedForType(type);
  if (!allowed || allowed.size === 0) return "equals";
  const first = VISIBILITY_OPERATOR_OPTIONS.find((item) =>
    allowed.has(item.value),
  );
  return first?.value ?? "equals";
}

export function visibilityActionLabel(action: VisibilityAction): string {
  return action === "hide" ? "Скрыть" : "Показать";
}

export function parseVisibilityListValue(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function looksLikeCityIdList(value: string): boolean {
  const items = parseVisibilityListValue(value);
  return items.length > 0 && items.every((item) => /^\d+$/.test(item));
}

export function displayCityVisibilityValue(
  value: string,
  names: Record<string, string>,
): string {
  return parseVisibilityListValue(value)
    .map((token) => names[token] ?? token)
    .join("\n");
}

export function formatVisibilitySummary({
  action,
  fieldLabel,
  operator,
  value,
}: {
  action: VisibilityAction;
  fieldLabel: string;
  operator: VisibilityOperator;
  value: string;
}): string {
  const verb = visibilityActionLabel(action);
  const field = fieldLabel.trim() || "другое поле";
  const op = visibilityOperatorLabel(operator);

  if (!visibilityOperatorNeedsValue(operator)) {
    return `${verb}, если «${field}» ${op}`;
  }

  if (operator === "in") {
    const items = parseVisibilityListValue(value);
    const list = items.length ? items.join(", ") : "…";
    return `${verb}, если «${field}» — одно из: ${list}`;
  }

  const shown = value.trim() || "…";
  return `${verb}, если «${field}» ${op} «${shown}»`;
}
